import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Row, Col, Card } from "react-bootstrap";
import { Autocomplete, TextField } from "@mui/material";
import {
    getAllCountryForSelect,
} from "modules/directory/country-master/services/country-master.service";
import {
    getStateByCountryId,
} from "modules/directory/state-master/services/state-master.service";
import { HttpStatus } from "modules/core";
import notistack from "modules/core/notistack";
import { DeleteCustomerAddress } from "modules/transactions/new-sales-entry/services/api.services";
import Select from 'react-select';
import { getCityListByStateId } from "modules/directory/city-master/services/city-master.service";

interface ShippingAddressModalProps {
    newCustomerForm: any;
    setNewCustomerForm: React.Dispatch<React.SetStateAction<any>>;
    currentCustomer?: any;
    setSelectedShippingId: any;
    selectedShippingId: any;
    customerHasRequiredFields: boolean;
    onSubmitCustomer: (updatedForm?: any) => void;   // Modified to accept optional parameter
    isSubmitting: boolean;
    closeModal: () => void;
    onSelect: (Set: string) => void;
}

interface OptionType {
    value: string;
    text: string;
    label: string;
}

interface AddressEntry {
    country: OptionType | null;
    state: OptionType | null;
    city: OptionType | null;
    address: string;
    zipCode: string;
    phoneNumber: string;
    email: string;
    id?: number;
    addressTypeId?: number;
}

const ShippingAddressModal: React.FC<ShippingAddressModalProps> = ({
    newCustomerForm,
    setNewCustomerForm,
    currentCustomer,
    setSelectedShippingId,
    selectedShippingId,
    customerHasRequiredFields,
    isSubmitting,
    onSubmitCustomer,
    closeModal,
    onSelect
}) => {
    const formRef = useRef<HTMLDivElement | null>(null);
    const [newAddressForm, setNewAddressForm] = useState<AddressEntry>({
        country: null,
        state: null,
        city: null,
        address: "",
        zipCode: "",
        phoneNumber: newCustomerForm?.mobileNo || "",
        email: newCustomerForm?.email || "",
        addressTypeId: 10,
    });
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [formErrors, setFormErrors] = useState<Partial<Record<keyof AddressEntry, string>>>({});

    const [countryList, setCountryList] = useState<OptionType[]>([]);
    const [stateList, setStateList] = useState<OptionType[]>([]);
    const [cityList, setCityList] = useState<OptionType[]>([]);

    const [resolvedNames, setResolvedNames] = useState<
        Record<number, { country: string; state: string; city: string }>
    >({});

    useEffect(() => {
        fetchCountries();
    }, []);

    useEffect(() => {
        if (newCustomerForm?.customerAddressList?.length) {
            newCustomerForm.customerAddressList?.forEach((addr: any) => {
                if (addr?.id && !resolvedNames[addr.id]) {
                    resolveAddressNames(addr);
                }
            });
        }
    }, [newCustomerForm?.customerAddressList, countryList]);

    const fetchCountries = async () => {
        try {
            const response = await getAllCountryForSelect();
            if (response.status === HttpStatus.Success) {
                const formattedCountries = response.data.map((item: any) => ({
                    value: item.value,
                    label: item.text,
                    text: item.text,
                }));
                setCountryList(formattedCountries);
            }
        } catch (e: any) {
            console.error("Error fetching countries", e);
        }
    };


    const fetchStates = async (countryId: string) => {
        try {
            const response = await getStateByCountryId(countryId);
            if (response.status === HttpStatus.Success) {
                const formattedCountries = response.data.map((item: any) => ({
                    value: item.value,
                    label: item.text,
                    text: item.text,
                }));
                setStateList(formattedCountries);
            }
        } catch (e: any) {
            console.error("Error fetching states", e);
        }
    };

    const resolveAddressNames = async (addr: any) => {
        try {
            if (!countryList.length) {
                await fetchCountries();
            }

            const countryName =
                countryList.find((c) => c.value === String(addr.countryId))?.text ?? "";

            let stateName = "";
            if (addr.countryId) {
                const stateResp = await getStateByCountryId(String(addr.countryId));
                if (stateResp.status === HttpStatus.Success) {
                    stateName =
                        stateResp.data.find((s: OptionType) => s.value === String(addr.stateId))
                            ?.text ?? "";
                }
            }

            setResolvedNames((prev: any) => ({
                ...prev,
                [addr.id ?? 0]: { country: countryName, state: stateName, city: addr.city || "" },
            }));
        } catch (err) {
            console.error("Error resolving address names", err);
        }
    };

    const handleChange = (field: keyof AddressEntry, value: any) => {
        setNewAddressForm((prev: any) => ({ ...prev, [field]: value }));
        setFormErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    useEffect(() => {
        if (newCustomerForm) {
            setNewAddressForm((prev) => ({
                ...prev,
                phoneNumber: newCustomerForm?.mobileNo || "",
                email: newCustomerForm?.email || "",
            }));
        }
    }, [newCustomerForm]);

    const handleCountryChange = (val: OptionType | null) => {
        handleChange("country", val);
        handleChange("state", null);
        handleChange("city", null);
        if (val) fetchStates(val.value);
    };


    const fetchCities = async (stateId: string) => {
        try {
            const response = await getCityListByStateId(stateId);
            if (response.status === HttpStatus.Success) {
                const formattedCities = response.data.map((item: any) => ({
                    value: item.value,
                    label: item.text,
                    text: item.text,
                }));
                setCityList(formattedCities);
            }
        } catch (e: any) {
            console.error("Error fetching cities", e);
        }
    };

    const handleStateChange = (val: OptionType | null) => {
        handleChange("state", val);
        handleChange("city", "");
        if (val) fetchCities(val.value);
    };

    const handleCityChange = (val: OptionType | null) => {
        handleChange("city", val);
    };

    const validateForm = () => {
        const errors: Partial<Record<keyof AddressEntry, string>> = {};
        if (!newAddressForm.country) errors.country = "Country is required";
        if (!newAddressForm.state) errors.state = "State is required";
        if (!newAddressForm.city) errors.city = "City is required";
        if (!newAddressForm.address?.trim()) errors.address = "Address is required";
        if (!newAddressForm.zipCode?.trim()) errors.zipCode = "Zip Code required";
        if (!newAddressForm.phoneNumber?.trim()) errors.phoneNumber = "Mobile number is required";
        if (newAddressForm.email?.trim()) {
            if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(newAddressForm.email)) {
                errors.email = "Invalid email format";
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };


    const resetForm = () => {
        setNewAddressForm({
            country: null,
            state: null,
            city: null,
            address: "",
            zipCode: "",
            phoneNumber: newCustomerForm?.mobileNo || "",
            email: newCustomerForm?.email || "",
            addressTypeId: 10,
        });
        setFormErrors({});
    };

    // const handleEditClick = async (id: number) => {
    //     const actualIndex = newCustomerForm.customerAddressList.findIndex(
    //         (addr: any) => addr.id === id
    //     );

    //     if (actualIndex === -1) return;
    //     const addr = newCustomerForm.customerAddressList[actualIndex];

    //     const countryOption =
    //         countryList.find((c: any) => c.value === String(addr.countryId)) ||
    //         (addr.countryId
    //             ? {
    //                 value: String(addr.countryId),
    //                 label: addr.country,
    //                 text: addr.country,
    //             }
    //             : null);

    //     let stateOption: OptionType | null = null;
    //     if (addr.stateId) {
    //         stateOption =
    //             stateList.find((s: any) => s.value === String(addr.stateId)) ||
    //             {
    //                 value: String(addr.stateId),
    //                 label: addr.state,
    //                 text: addr.state,
    //             };
    //     }

    //     setNewAddressForm({
    //         country: countryOption,
    //         state: stateOption,
    //         city: addr.city || "",
    //         address: addr.address,
    //         zipCode: addr.zipCode,
    //         phoneNumber: addr.phoneNumber,
    //         email: addr.email,
    //         id: addr.id,
    //         addressTypeId: addr.addressTypeId ?? 10,
    //     });

    //     setEditingIndex(actualIndex);
    //     if (countryOption) {
    //         const stateResp = await getStateByCountryId(countryOption.value);
    //         if (stateResp.status === HttpStatus.Success) {
    //             const formattedCountries = stateResp.data.map((item: any) => ({
    //                 value: item.value,
    //                 label: item.text,
    //                 text: item.text,
    //             }));
    //             setStateList(formattedCountries);
    //             const matchedState =
    //                 formattedCountries.find(
    //                     (s: any) => s.value === String(addr.stateId)
    //                 ) ||
    //                 (addr.stateId
    //                     ? {
    //                         value: String(addr.stateId),
    //                         label: addr.state,
    //                         text: addr.state,
    //                     }
    //                     : null)
    //             setNewAddressForm((prev) => ({
    //                 ...prev,
    //                 state: matchedState,
    //             }));
    //         }
    //     }
    //     setFormErrors({});

    //     setTimeout(() => {
    //         formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    //     }, 100);

    // };

    const handleDelete = async (index: number, id: any) => {
        if (newCustomerForm?.id) {
            const response = await DeleteCustomerAddress(id)
            if (response.status === HttpStatus.Success) {
                const updated = newCustomerForm.customerAddressList.filter(
                    (_: any, i: number) => i !== index
                );
                setNewCustomerForm((prev: any) => ({
                    ...prev,
                    customerAddressList: updated,
                }));
                notistack.notifyFromApi(response)
            } else {
                notistack.notifyFromApi(response)
            }
        } else {
            const updated = newCustomerForm.customerAddressList.filter(
                (_: any, i: number) => i !== index
            );
            setNewCustomerForm((prev: any) => ({
                ...prev,
                customerAddressList: updated,
            }));
        }
    };

    const handleSelect = (id: number) => {
        localStorage.setItem('bid', String(id))
        setSelectedShippingId(id);
        // onSelect('customer')
        // closeModal()
    };

    const handleSaveAndSubmit = () => {
        if (!validateForm()) return;

        const updatedList = [...(newCustomerForm.customerAddressList || [])];

        const formattedAddress = {
            id: editingIndex !== null ? updatedList[editingIndex].id ?? 0 : 0,
            address: newAddressForm.address,
            zipCode: newAddressForm.zipCode,
            phoneNumber: newAddressForm.phoneNumber,
            ...(newAddressForm.email?.trim() ? { email: newAddressForm.email.trim() } : {}),
            addressTypeId: newAddressForm.addressTypeId ?? 10,
            customerId: currentCustomer?.id ?? 0,
            centralId: 0,
            countryId: newAddressForm.country?.value
                ? parseInt(newAddressForm.country.value)
                : 0,
            country: newAddressForm.country?.text || "",
            stateId: newAddressForm.state?.value
                ? parseInt(newAddressForm.state.value)
                : 0,
            state: newAddressForm.state?.text || "",
            city: newAddressForm.city?.text || "",
            cityId: newAddressForm.city?.value
                ? parseInt(newAddressForm.city.value)
                : 0,
        };

        if (editingIndex !== null) {
            updatedList[editingIndex] = formattedAddress;
            setEditingIndex(null);
        } else {
            updatedList.push(formattedAddress);
        }

        const updatedCustomerForm = {
            ...newCustomerForm,
            customerAddressList: updatedList,
        };

        setNewCustomerForm(updatedCustomerForm);

        setSelectedShippingId(null);
        onSubmitCustomer(updatedCustomerForm);

        resetForm();
    };

    const bid = Number(localStorage.getItem('bid'))

    return (
        <div ref={formRef}>
            {customerHasRequiredFields ? (
                <Card className="mb-3 shadow-none">
                    <Card.Body>
                        <Row className="mb-3">
                            <Col md={4}>
                                <Form.Label>Mobile No. <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newAddressForm.phoneNumber}
                                    maxLength={10}
                                    placeholder="Enter 10-digit mobile number"
                                    onChange={(e: any) => {
                                        const val = e.target.value;
                                        if (/^\d{0,10}$/.test(val)) {
                                            handleChange("phoneNumber", val);

                                            setFormErrors((prev: any) => ({
                                                ...prev,
                                                phoneNumber:
                                                    val.length === 0
                                                        ? "Mobile number is required"
                                                        : val.length !== 10
                                                            ? "Mobile number must be 10 digits"
                                                            : /^(\d)\1{9}$/.test(val)
                                                                ? "Mobile number cannot have all digits the same"
                                                                : ""
                                            }));
                                        }
                                    }}
                                    isInvalid={!!formErrors.phoneNumber}
                                    className="form-control-sm"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.phoneNumber}
                                </Form.Control.Feedback>
                            </Col>

                            <Col md={4}>
                                <Form.Label>Email </Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="Enter email"
                                    value={newAddressForm.email}
                                    onChange={(e: any) => {
                                        const val = e.target.value;
                                        handleChange("email", val);
                                        setFormErrors((prev: any) => ({
                                            ...prev,
                                            email: val && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(val)
                                                ? "Invalid email format"
                                                : "",
                                        }));
                                    }}
                                    isInvalid={!!formErrors.email}
                                    className="form-control-sm"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.email}
                                </Form.Control.Feedback>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <div className="col-sm-4">
                                <label>
                                    Country <span className="text-danger">*</span>
                                </label>
                                {/* <select
                                    className={`form-control form-control-sm ${formErrors.country ? "is-invalid" : ""}`}

                                    value={newAddressForm.country?.value || ""}
                                    onChange={(e) => {
                                        const selected = countryList.find((c: any) => String(c.value) === e.target.value) || null;
                                        handleCountryChange(selected);
                                    }}
                                >
                                    <option value="">-- Select Country --</option>
                                    {countryList.map((c: any) => (
                                        <option key={c.value} value={c.value}>
                                            {c.text}
                                        </option>
                                    ))}
                                </select> */}
                                <Select
                                    classNamePrefix="react-select"
                                    placeholder="-- Select Country --"
                                    options={countryList}
                                    value={newAddressForm.country || null}
                                    onChange={(selected: any) => handleCountryChange(selected)}
                                    isClearable
                                    isSearchable
                                    styles={{
                                        control: (base: any) => ({
                                            ...base,
                                            minHeight: "26px",
                                            fontSize: "11px",
                                            fontWeight: 400,
                                            fontStyle: "normal",
                                            lineHeight: "17px",
                                            color: "rgb(15, 17, 20)",
                                            fontFamily:
                                                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                                            border: "1px solid #ced4da",
                                            borderRadius: "4px",
                                            boxShadow: "none",
                                            backgroundColor: "#fff",
                                            "&:hover": {
                                                border: "1px solid #ced4da",
                                            },
                                        }),
                                        dropdownIndicator: (base: any) => ({
                                            ...base,
                                            padding: "2px",
                                        }),
                                        clearIndicator: (base: any) => ({
                                            ...base,
                                            padding: "2px",
                                        }),
                                        valueContainer: (base: any) => ({
                                            ...base,
                                            padding: "0 6px",
                                        }),
                                        input: (base: any) => ({
                                            ...base,
                                            margin: 0,
                                            padding: 0,
                                        }),
                                        menu: (base: any) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                    }}
                                />
                                {formErrors.country && (
                                    <span className="text-danger" style={{ fontSize: "11px" }}>
                                        {formErrors.country}
                                    </span>
                                )}
                            </div>
                            <div className="col-sm-4">
                                <label>
                                    State <span className="text-danger">*</span>
                                </label>
                                {/* <select
                                    className={`form-control form-control-sm ${formErrors.state ? "is-invalid" : ""}`}
                                    value={newAddressForm.state?.value || ""}
                                    onChange={(e) => {
                                        const selected = stateList.find((s: any) => String(s.value) === e.target.value) || null;
                                        handleStateChange(selected);
                                    }}
                                    disabled={!newAddressForm.country}
                                >
                                    <option value="">-- Select State --</option>
                                    {stateList.map((s: any) => (
                                        <option key={s.value} value={s.value}>
                                            {s.text}
                                        </option>
                                    ))}
                                </select> */}
                                <Select
                                    classNamePrefix="react-select"
                                    placeholder="-- Select State --"
                                    options={stateList}
                                    value={newAddressForm.state || ""}
                                    onChange={(selected: any) => handleStateChange(selected)}
                                    isClearable
                                    isSearchable
                                     isDisabled={!newAddressForm.country}
                                    styles={{
                                        control: (base: any) => ({
                                            ...base,
                                            minHeight: "26px",
                                            fontSize: "11px",
                                            fontWeight: 400,
                                            fontStyle: "normal",
                                            lineHeight: "17px",
                                            color: "rgb(15, 17, 20)",
                                            fontFamily:
                                                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                                            border: "1px solid #ced4da",
                                            borderRadius: "4px",
                                            boxShadow: "none",
                                            backgroundColor: "#fff",
                                            "&:hover": {
                                                border: "1px solid #ced4da",
                                            },
                                        }),
                                        dropdownIndicator: (base: any) => ({
                                            ...base,
                                            padding: "2px",
                                        }),
                                        clearIndicator: (base: any) => ({
                                            ...base,
                                            padding: "2px",
                                        }),
                                        valueContainer: (base: any) => ({
                                            ...base,
                                            padding: "0 6px",
                                        }),
                                        input: (base: any) => ({
                                            ...base,
                                            margin: 0,
                                            padding: 0,
                                        }),
                                        menu: (base: any) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                    }}
                                />
                                {formErrors.state && (
                                    <span className="text-danger" style={{ fontSize: "11px" }}>
                                        {formErrors.state}
                                    </span>
                                )}
                            </div>
                            <div className="col-sm-4">
                                <label>
                                    City <span className="text-danger">*</span>
                                </label>
                                {/* <input
                                    type="text"
                                    className={`form-control form-control-sm ${formErrors.city ? "is-invalid" : ""}`}
                                    placeholder="Enter city name"
                                    value={newAddressForm.city}
                                    maxLength={20}
                                    onChange={handleCityChange}
                                /> */}
                                <Select
                                    classNamePrefix="react-select"
                                    placeholder="-- Select City --"
                                    options={cityList}
                                    value={newAddressForm.city}
                                    onChange={(selected: any) => handleCityChange(selected)}
                                    isClearable
                                    isSearchable
                                    isDisabled={!newAddressForm.state}
                                    styles={{
                                        control: (base: any) => ({
                                            ...base,
                                            minHeight: "26px",
                                            fontSize: "11px",
                                            fontWeight: 400,
                                            fontStyle: "normal",
                                            lineHeight: "17px",
                                            color: "rgb(15, 17, 20)",
                                            fontFamily:
                                                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                                            border: "1px solid #ced4da",
                                            borderRadius: "4px",
                                            boxShadow: "none",
                                            backgroundColor: "#fff",
                                            "&:hover": {
                                                border: "1px solid #ced4da",
                                            },
                                        }),
                                        dropdownIndicator: (base: any) => ({
                                            ...base,
                                            padding: "2px",
                                        }),
                                        clearIndicator: (base: any) => ({
                                            ...base,
                                            padding: "2px",
                                        }),
                                        valueContainer: (base: any) => ({
                                            ...base,
                                            padding: "0 6px",
                                        }),
                                        input: (base: any) => ({
                                            ...base,
                                            margin: 0,
                                            padding: 0,
                                        }),
                                        menu: (base: any) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                    }}
                                />
                                {formErrors.city && (
                                    <span className="text-danger" style={{ fontSize: "11px" }}>
                                        {formErrors.city}
                                    </span>
                                )}
                            </div>
                        </Row>

                        {/* Address + Zip */}
                        <Row className="mb-3">
                            <Col md={4}>
                                <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    className="form-control-sm"
                                    placeholder="Enter address"
                                    value={newAddressForm.address}
                                    maxLength={100}
                                    onChange={(e: any) => {
                                        const val = e.target.value;
                                        if (val.length <= 100) {
                                            handleChange("address", val);
                                            setFormErrors((prev: any) => ({
                                                ...prev,
                                                address: val.trim() === "" ? "Address is required" : "",
                                            }));
                                        }
                                    }}
                                    isInvalid={!!formErrors.address}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.address}
                                </Form.Control.Feedback>
                            </Col>

                            <Col md={4}>
                                <Form.Label>
                                    Zip Code <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newAddressForm.zipCode}
                                    placeholder="Enter zip code"
                                    className="form-control-sm"
                                    maxLength={6}
                                    onChange={(e: any) => {
                                        const val = e.target.value;
                                        if (/^\d{0,6}$/.test(val)) {
                                            if (val.length === 1 && val[0] === "0") {
                                                return;
                                            }
                                            handleChange("zipCode", val);
                                            setFormErrors((prev: any) => {
                                                let error = "";
                                                if (val.length === 0) {
                                                    error = "Zip code required";
                                                } else if (val.length !== 6) {
                                                    error = "Zip code must be 6 digits";
                                                }
                                                return {
                                                    ...prev,
                                                    zipCode: error,
                                                };
                                            });
                                        }
                                    }}
                                    isInvalid={!!formErrors.zipCode}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.zipCode}
                                </Form.Control.Feedback>
                            </Col>
                        </Row>

                        <div className="text-end">
                            <button
                                type="button"
                                onClick={handleSaveAndSubmit}
                                disabled={isSubmitting}
                                style={{
                                    backgroundColor: "#3498db",
                                    border: "none",
                                    color: "#fff",
                                    padding: "6px 16px",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                }}
                            >
                                {isSubmitting ? "Saving..." : editingIndex !== null ? "Update Address" : "Add Address"}
                            </button>
                        </div>
                    </Card.Body>
                </Card>
            ) : (
                <div className="alert alert-warning">
                    Please enter required information for the customer (First Name, Last Name, and Mobile No)
                </div>
            )}

            <div className="row">
                {newCustomerForm?.customerAddressList?.some((addr: any) => addr.addressTypeId === 10) && (
                    <>
                        <div className="col-12 mb-2">
                            <h5>Billing Address</h5>
                        </div>
                        {newCustomerForm.customerAddressList
                            .filter((addr: any) => addr.addressTypeId === 10)
                            .map((addr: any, idx: any) => (
                                <div className="col-md-6 mb-3" key={`billing-${idx}`}>
                                    <div className="card h-100 shadow-sm border rounded position-relative">
                                        {addr.id !== 0 && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-2">
                                                <input
                                                    type="radio"
                                                    name="selectedAddress"
                                                    value={addr.id ?? 0}
                                                    checked={bid !== null && bid === (addr.id ?? 0)}
                                                    onChange={() => handleSelect(addr.id ?? 0)}
                                                />
                                            </div>
                                        )}

                                        <div className="card-body">
                                            <div className="d-flex flex-column gap-1 mb-2">
                                                <p className="mb-0"><strong>Phone:</strong> {addr?.phoneNumber}</p>
                                                {addr?.email?.trim() ? <p className="mb-0"><strong>email:</strong> {addr?.email}</p> : null}
                                            </div>

                                            <p className="mb-1">
                                                <strong>Address:</strong> {addr.address}
                                            </p>

                                            <h6 className="card-title">
                                                {addr.city || ""}, {addr.state || ""},  {addr.country || ""}
                                            </h6>

                                            <p className="mb-1">
                                                <strong>Zip Code:</strong> {addr.zipCode}
                                            </p>

                                            {/* <Button
                                                size="sm"
                                                variant="primary"
                                                onClick={() => handleEditClick(addr.id)}
                                            >
                                                Edit
                                            </Button>{" "} */}

                                            <Button
                                                size="sm"
                                                variant="danger"
                                                onClick={() => handleDelete(idx, addr.id)}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </>
                )}

                {newCustomerForm?.customerAddressList?.some((addr: any) => addr.addressTypeId === 2) && (
                    <>
                        <div className="col-12 mb-2">
                            <h5>Shipping Address</h5>
                        </div>
                        {newCustomerForm.customerAddressList
                            .filter((addr: any) => addr.addressTypeId === 2)
                            .map((addr: any, idx: any) => (
                                <div className="col-md-6 mb-3" key={`shipping-${idx}`}>
                                    <div className="card h-100 shadow-sm border rounded position-relative">


                                        <div className="card-body">
                                            <div className="d-flex flex-column gap-1 mb-2">
                                                <p className="mb-0"><strong>Phone:</strong> {addr?.phoneNumber}</p>
                                                <p className="mb-0"><strong>email:</strong> {addr?.email}</p>
                                            </div>
                                            <p className="mb-1">
                                                <strong>Address:</strong> {addr.address}
                                            </p>

                                            <h6 className="card-title">
                                                {addr.city || ""}, {addr.state || ""},  {addr.country || ""}
                                            </h6>

                                            <p className="mb-1">
                                                <strong>Zip Code:</strong> {addr.zipCode}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </>
                )}
            </div>
        </div>
    );
};

export default ShippingAddressModal;